import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Contextos
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

// API
import { useParticipanteService } from '../../services/participanteServices';

// Componentes
import CountryPhoneSelect from '../../components/CountryPhoneSelect/CountryPhoneSelect';

// Utils
import {
  formatLimitedPhoneForCountry,
  getDefaultPhoneCountry,
  getPhoneInputMaxLength,
  isPossiblePhoneForCountry,
} from '../../utils/phoneCountries';

// Estilos
import styles from './participantecadastropage.module.css';

const DEBUG = false;
const dbg = (...args) => { if (DEBUG) console.log('[ParticipanteCadastroPage]', ...args); };

/**
 * Le state.from e tenta inferir:
 * - turmaHash
 * - dest ('res' | 'ead')
 * Aceita /res/:hash, /responder/:hash e /ead/:hash
 */
function parseFrom(location) {
  const from = location?.state?.from || null;
  const pathname = from?.pathname || '';
  const search = from?.search || '';
  const hash = from?.hash || '';

  const match = pathname.match(/^\/(res(?:ponder)?|ead)\/([^/?#]+)/i);
  const rawDest = match?.[1] || null;
  const turmaHash = match?.[2] || null;

  const dest = rawDest
    ? (rawDest.toLowerCase().startsWith('ead') ? 'ead' : 'res')
    : '';

  return { from, pathname, search, hash, turmaHash, dest };
}

/** Hook para ler ?turma=...&dest=... da query string */
function useQueryParams(location) {
  return useMemo(() => {
    try {
      const usp = new URLSearchParams(location.search || '');
      const turma = (usp.get('turma') || '').trim();
      const dest = (usp.get('dest') || '').trim().toLowerCase();
      return {
        turma: turma || '',
        dest: (dest === 'res' || dest === 'ead') ? dest : '',
      };
    } catch {
      return { turma: '', dest: '' };
    }
  }, [location.search]);
}

/**
 * Decide para onde voltar apos cadastro+login:
 * 1) se tiver state.from.pathname -> volta pra la
 * 2) senao, se tiver turma + dest (res|ead) -> volta /dest/turma
 * 3) senao -> /participante/page
 */
function getRedirectPath(location, turma, dest) {
  const fromPath = location?.state?.from?.pathname || '';
  if (fromPath) return fromPath;

  if (turma && (dest === 'res' || dest === 'ead')) {
    return `/${dest}/${encodeURIComponent(turma)}`;
  }

  return '/participante/page';
}

function ParticipanteCadastroPage() {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const { loginParticipante } = useAuth();
  const { criarParticipante } = useParticipanteService();

  const [selectedCountry, setSelectedCountry] = useState(() => getDefaultPhoneCountry());
  const celularMaxLength = useMemo(
    () => getPhoneInputMaxLength(selectedCountry),
    [selectedCountry]
  );

  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [celular, setCelular] = useState('');
  const [senha, setSenha] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const notFoundToastShownRef = useRef(false);

  // Query: ?turma=...&dest=...
  const qp = useQueryParams(location);

  // State.from: /res/:hash ou /ead/:hash
  const fromInfo = useMemo(() => parseFrom(location), [location]);

  // Efetivos (prioriza query; depois state.from)
  const turmaHashEfetivo = useMemo(
    () => qp.turma || fromInfo.turmaHash || '',
    [qp.turma, fromInfo.turmaHash]
  );

  const destEfetivo = useMemo(
    () => qp.dest || fromInfo.dest || '',
    [qp.dest, fromInfo.dest]
  );

  const handleCelularChange = (e) => {
    const valor = e.target.value;
    setCelular(formatLimitedPhoneForCountry(valor, selectedCountry));
  };

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setCelular((prev) => formatLimitedPhoneForCountry(prev, country));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCarregando(true);
    setErro('');

    const celularSemMascara = celular.replace(/\D/g, '');

    if (!isPossiblePhoneForCountry(celularSemMascara, selectedCountry)) {
      showToast('O telefone esta incompleto para o pais selecionado.', 3000, 'error');
      setErro('O telefone esta incompleto para o pais selecionado.');
      setCarregando(false);
      return;
    }

    // No fluxo de convite via /res ou /ead, turma deve existir para criar ja vinculado
    if (!turmaHashEfetivo) {
      showToast(
        'Não foi possível identificar a turma. Acesse o link correto (convite) ou peça um novo.',
        5000,
        'error'
      );
      setCarregando(false);
      return;
    }

    const celularComDDI = `${selectedCountry.ddi}${celularSemMascara}`;

    const dados = {
      nome,
      email: email || '',
      celular: celularComDDI,
      senha,
      loginPor: 'celular',
      idTurma: turmaHashEfetivo, // hash criptografado da turma
    };

    dbg('payload criarParticipante:', dados);

    try {
      const response = await criarParticipante(dados);
      dbg('criarParticipante response:', response);

      if (response?.success) {
        showToast('Cadastro realizado com sucesso!', 3000, 'success');

        // Login automatico
        const loginResult = await loginParticipante(celularComDDI, senha);
        dbg('loginParticipante result:', loginResult);

        if (loginResult?.success) {
          const destino = getRedirectPath(location, turmaHashEfetivo, destEfetivo || 'res');
          dbg('Cadastro+login OK -> navegando para:', destino);
          navigate(destino, { replace: true });
        } else {
          showToast('Erro ao fazer login automatico.', 3000, 'error');
          setErro('Erro ao fazer login automatico.');
        }
      } else {
        showToast(response?.message || 'Erro ao cadastrar participante', 4000, 'error');
        setErro(response?.message || 'Erro ao cadastrar participante');
      }
    } catch (error) {
      dbg('criarParticipante error:', error);
      showToast('Erro ao conectar com o servidor.', 4000, 'error');
      setErro('Erro ao conectar com o servidor.');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (!DEBUG) return;
    dbg('mount -> location:', location);
    dbg('mount -> fromInfo:', fromInfo);
    dbg('mount -> query turma/dest:', qp);
    dbg('mount -> turmaHashEfetivo:', turmaHashEfetivo);
    dbg('mount -> destEfetivo:', destEfetivo);
  }, [location, fromInfo, qp, turmaHashEfetivo, destEfetivo]);

  useEffect(() => {
    if (!location.state?.participanteNaoEncontrado || notFoundToastShownRef.current) return;

    notFoundToastShownRef.current = true;
    showToast(
      'Não encontramos um participante com esse celular. Preencha seu cadastro para continuar.',
      4000,
      'default'
    );

    const nextState = { ...(location.state || {}) };
    delete nextState.participanteNaoEncontrado;
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: nextState,
    });
  }, [location.pathname, location.search, location.state, navigate, showToast]);

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Cadastro de Participante</h2>

      {DEBUG && (
        <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
          <div><strong>from.pathname:</strong> {fromInfo.pathname || '(vazio)'}</div>
          <div><strong>query.turma:</strong> {qp.turma || '(vazio)'}</div>
          <div><strong>query.dest:</strong> {qp.dest || '(vazio)'}</div>
          <div><strong>from.turmaHash:</strong> {fromInfo.turmaHash || '(vazio)'}</div>
          <div><strong>from.dest:</strong> {fromInfo.dest || '(vazio)'}</div>
          <div><strong>efetivo.turma:</strong> {turmaHashEfetivo || '(vazio)'}</div>
          <div><strong>efetivo.dest:</strong> {destEfetivo || '(vazio)'}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <input
          type="text"
          placeholder="Nome completo"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          className={styles.input}
          required
        />

        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={styles.input}
          required
        />

        <div className={styles.phoneRow}>
          <CountryPhoneSelect
            value={selectedCountry}
            onChange={handleCountryChange}
            label="Pais"
            disabled={carregando}
          />

          <input
            type="text"
            placeholder={selectedCountry.placeholder}
            value={celular}
            onChange={handleCelularChange}
            className={styles.input}
            required
            inputMode="tel"
            maxLength={celularMaxLength}
          />
        </div>

        <input
          type="password"
          placeholder="Senha"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          className={styles.input}
          required
        />

        <button type="submit" className={styles.button} disabled={carregando}>
          {carregando ? 'Cadastrando...' : 'Cadastrar'}
        </button>

        {erro && <p className={styles.error}>{erro}</p>}

        <p>Informe seus dados para que criemos seu registro!</p>
      </form>
    </div>
  );
}

export default ParticipanteCadastroPage;
